import React, { useState, useEffect, useCallback } from 'react'
import { Pagination, Row, Col, Collapse, Drawer, Modal, message, Button, Table } from 'antd'
import moment from 'moment'
import { CaretRightOutlined, InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import axios from 'axios'

import PageHeader from '../Layout/PageHeader'
import OrdersList from './OrdersList'
import OrderButtons from './OrderButtons'
import OrderFilters from './OrderFilters'
import DragUpload from "../Form/DragUpload";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import { getFeatureFlag } from '../../config/featureFlag'

const today = moment()
const oneWeekAgo = moment().subtract(7, 'days')

const downloadTemplate = async () => {
  try {
    const { data } = await axios({
      url: "/api/order/template/bulkCancelTemplate",
      method: "GET",
      responseType: "blob",
      timeout: 1000 * 300
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "template_bulk_cancel.xlsx");
    document.body.appendChild(link);
    link.click();
  } catch (err) {
    console.log("err", err);
  }
}

const AllOrder = () => {
  const { permissions } = usePermissions();

  const [orders, setOrders] = useState([])
  const [statusMaps, setStatusMaps] = useState({})
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [bulkCancel, setBulkCancel] = useState();
  const [bulkUpdateStatusPreview, setBulkUpdateStatusPreview] = useState();
  const [bulkUpdateStatusResults, setBulkUpdateStatusResults] = useState();
  const [filters, setFilters] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const internalStatusParam = urlParams.get('internalstatus');
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');

    return {
      isRemittance: null,
      completeDelivered: null,
      startDate: null,
      endDate: null,
      paymentStartDate: startDateParam ? moment(startDateParam) : oneWeekAgo,
      paymentEndDate: endDateParam ? moment(endDateParam) : today,
      pickedUpStartDate: undefined,
      pickedUpEndDate: undefined,
      sortBy: "paymentDate",
      internalStatus: internalStatusParam ? internalStatusParam.split(',') : undefined,
    };
  });


  const fetchOrders = useCallback(async () => {
    setIsLoading(true)

    const params = {
      ...(filters.startDate !== null) && { startDate: filters.startDate?.startOf('day')?.toISOString() },
      ...(filters.endDate !== null) && { endDate: filters.endDate?.endOf('day')?.toISOString() },
      ...(filters.paymentStartDate !== null) && { paymentStartDate: filters.paymentStartDate?.startOf('day')?.toISOString() },
      ...(filters.paymentEndDate !== null) && { paymentEndDate: filters.paymentEndDate?.endOf('day')?.toISOString() },
      ...(filters.pickedUpStartDate !== null) && { pickedUpStartDate: filters.pickedUpStartDate?.startOf('day')?.toISOString() },
      ...(filters.pickedUpEndDate !== null) && { pickedUpEndDate: filters.pickedUpEndDate?.endOf('day')?.toISOString() },
      ...(filters.channel) && { channel: filters.channel },
      ...(filters.search && { searchField: filters.searchField, search: filters.search }),
      ...(filters.serviceProvider && { serviceProvider: filters.serviceProvider }),
      ...(filters.internalStatus && { internalStatus: filters.internalStatus }),
      limit: pagination.limit,
      page: pagination.currentPage,
      ...(filters.sortBy !== null) && { sortBy: filters.sortBy },
    }

    const res = await axios.get("/api/orders", { params })

    setOrders(res.data.orders)
    setPagination(res.data.pagination)
    setIsLoading(false)
  }, [pagination.currentPage, pagination.limit, filters])

  useEffect(() => {
    fetchOrders()
  }, [pagination.currentPage, pagination.limit, fetchOrders])

  useEffect(() => {
    const getStatusMaps = async () => {
      const statusRes = await axios.get('api/order/get-status-maps')
      setStatusMaps(statusRes.data.data)
    }

    getStatusMaps()
  }, [])

  const uploadSuccessHandler = (res) => {
    const trackingNo = res.data.data;
    Modal.confirm({
      title: `Are you sure to bulk cancel the order in this file ?`,
      onOk() {
        axios
          .post(`/api/order/bulk/cancel`, {
            params: {
              trackingNo: trackingNo,
            },
          })
          .then((res) => {
            setBulkCancel(res.data.data);
            message.info("The bulk parcel cancelation process is done");
          })
          .catch((error) => {
            message.error(error.response.data.message || error.message);
          });
      },
    });
  };
  const searchUploadSuccessHandler = (res) => {
    message.info(`Please check the Export progress in System -> Jobs`);
  }

  const bulkPreviewHandler = (res) => {
    setBulkUpdateStatusResults(null)
    setBulkUpdateStatusPreview(res.data.data)
  }

  const confirmBulkUpdateStatus = async () => {
    try {
      const res = await axios.post("/api/order/bulk/update-status", { preview: bulkUpdateStatusPreview })
      setBulkUpdateStatusResults(res.data.data)
      setBulkUpdateStatusPreview(null)
      message.info("Bulk status update completed.")
      fetchOrders()
    } catch (error) {
      message.error(error.response?.data?.message || error.message)
    }
  }

  const downloadBulkUpdateTemplate = async () => {
    try {
      const { data } = await axios({
        url: "/api/order/template/bulkUpdateStatusTemplate",
        method: "GET",
        responseType: "blob",
        timeout: 1000 * 300
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Template_Bulk_Update_Status.xlsx");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.log("err", err);
    }
  }

  return (
    <div>
      <PageHeader 
        title="All Order"
        childRoutes={[{ path: "/all-order", breadcrumbName: "All Order" }]}
        additionalInfo={() => 
          <React.Fragment>
            <InfoCircleOutlined onClick={() => setDrawerVisible(true)} />
            <Drawer title="Internal Status Mappings" placement="right" onClose={() => setDrawerVisible(false)} visible={drawerVisible} width={736}>
              <p>Internal statuses of Orders are mapped from Couriers statuses as follows:</p>
              { Object.keys(statusMaps).map(courier => {
                return (
                  <React.Fragment>
                    <h2>{courier.toUpperCase()}</h2>
                    <ul>
                    { Object.keys(statusMaps[courier]).map(externalStatus => {
                      return (
                        <li>{externalStatus} ➜ {statusMaps[courier][externalStatus]}</li>
                      )
                    }) }
                    </ul>
                  </React.Fragment>
                );
              }) }
            </Drawer>
          </React.Fragment>
        }
      />
      {permissions?.includes(PERMISSIONS.BULK_CANCEL) && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Bulk Cancel Order" key="bulkCancel">
            {bulkCancel && (
              <Collapse
                className="mt-2"
                size="small"
                expandIcon={({ isActive }) => (
                  <CaretRightOutlined rotate={isActive ? 90 : 0} />
                )}
              >
                <Collapse.Panel
                  header={
                    <span>
                      <InfoCircleOutlined /> Results of Last Bulk
                    </span>
                  }
                  key="last-bulk-result"
                >
                  <h4>
                    <strong>Refunded</strong>
                  </h4>
                  <ol>
                    {bulkCancel
                      .filter((result) => result.success)
                      .map((success) => (
                        <li key={success.consign_no}>
                          {success.consign_no}: RM {success.message}
                        </li>
                      ))}
                  </ol>
                  <h4>
                    <strong>Failed</strong>
                  </h4>
                  <ol>
                    {bulkCancel
                      .filter((result) => !result.success)
                      .map((fail) => (
                        <li key={fail.consign_no}>
                          {fail.consign_no}: {fail.message}
                        </li>
                      ))}
                  </ol>
                </Collapse.Panel>
              </Collapse>
            )}

            <DragUpload
              action={`${process.env.REACT_APP_SERVER_URL}/api/order/bulk/beforeCancel`}
              accept=".xls, .xlsx"
              method="post"
              onUploadSuccess={(res) => uploadSuccessHandler(res)}
            />
            <br />
            <Button onClick={downloadTemplate} className="mt-2">
              <DownloadOutlined /> Template
            </Button>
          </Collapse.Panel>
        </Collapse>
      )}

      {permissions?.includes(PERMISSIONS.BULK_SEARCH_ORDER) && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Bulk Search Order" key="bulkSearch">
            <DragUpload
              action={`${process.env.REACT_APP_SERVER_URL}/api/order/bulk/search/csv`}
              accept=".xls, .xlsx"
              method="post"
              onUploadSuccess={(res) => searchUploadSuccessHandler(res)}
              additionalDesc={`Excel file with "Search Tracking Number" columns.`}
            />
            <br />
          </Collapse.Panel>
        </Collapse>
      )}

      {permissions?.includes(PERMISSIONS.UPDATE_ORDER_STATUS) && getFeatureFlag("updateOrderStatus") && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Bulk Update Status" key="bulkUpdateStatus">
            {bulkUpdateStatusPreview && (
              <div className="mb-2">
                {bulkUpdateStatusPreview.some((p) => p.error) && (
                  <p style={{ color: "#faad14", marginBottom: 12 }}>
                    Some records have errors. They will be skipped.
                  </p>
                )}
                <Table
                  size="small"
                  dataSource={bulkUpdateStatusPreview}
                  columns={[
                    { title: "Tracking No", dataIndex: "trackingNo" },
                    { title: "Current Status", dataIndex: "currentStatus" },
                    { title: "New Status", dataIndex: "newStatus" },
                    {
                      title: "Error",
                      dataIndex: "error",
                      render: (text) => text && <span style={{ color: "red" }}>{text}</span>,
                    },
                  ]}
                  pagination={false}
                  rowKey={(row, index) => row.trackingNo || index}
                />
                <Button type="primary" onClick={confirmBulkUpdateStatus} className="mt-2 mb-2">
                  Confirm Update
                </Button>
              </div>
            )}
            {bulkUpdateStatusResults && (
              <Table
                className="mb-2"
                size="small"
                dataSource={bulkUpdateStatusResults}
                columns={[
                  { title: "Tracking No", dataIndex: "trackingNo" },
                  {
                    title: "Result",
                    dataIndex: "success",
                    render: (success, record) => {
                      if (record.skipped) return "Skipped (already in status)"
                      if (success) return record.priceChanged ? "Updated (price changed)" : "Updated"
                      return <span style={{ color: "red" }}>{record.message}</span>
                    },
                  },
                ]}
                pagination={false}
                rowKey={(row, index) => row.trackingNo || index}
              />
            )}
            <DragUpload
              action={`${process.env.REACT_APP_SERVER_URL}/api/order/bulk/update-status/preview`}
              accept=".xls, .xlsx"
              method="post"
              onUploadSuccess={(res) => bulkPreviewHandler(res)}
              additionalDesc={`Excel file with "Tracking No" and "Status" columns. Max 50 records.`}
            />
            <br />
            <Button onClick={downloadBulkUpdateTemplate} className="mt-2">
              <DownloadOutlined /> Template
            </Button>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
        <Collapse.Panel header="Filters" key="filters">
          <OrderFilters
            filters={filters}
            onFiltersChange={filters => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row
          className="mt-2 mb-2"
          justify="space-between"
          align="middle"
        >
        <OrderButtons
            selectedRowKeys={selectedRows}
            orders={orders}
            filters={{
              ...(filters.startDate !== null && {
                startDate: filters.startDate?.startOf("day")?.toISOString(),
              }),
              ...(filters.endDate !== null && {
                endDate: filters.endDate?.endOf("day")?.toISOString(),
              }),
              ...(filters.paymentStartDate !== null && {
                paymentStartDate: filters.paymentStartDate
                  ?.startOf("day")
                  ?.toISOString(),
              }),
              ...(filters.paymentEndDate !== null && {
                paymentEndDate: filters.paymentEndDate
                  ?.endOf("day")
                  ?.toISOString(),
              }),
              ...(filters.pickedUpStartDate !== null && {
                pickedUpStartDate: filters.pickedUpStartDate
                  ?.startOf("day")
                  ?.toISOString(),
              }),
              ...(filters.pickedUpEndDate !== null && {
                pickedUpEndDate: filters.pickedUpEndDate
                  ?.endOf("day")
                  ?.toISOString(),
              }),
              ...(filters.channel && { channel: filters.channel }),
              ...(filters.search && {
                searchField: filters.searchField,
                search: filters.search,
              }),
              ...(filters.serviceProvider && {
                serviceProvider: filters.serviceProvider,
              }),
              ...(filters.internalStatus && {
                internalStatus: filters.internalStatus,
              }),
              pagination: pagination,
            }}
            onRefunded={() => fetchOrders()}
            
          />

        <Col>
          <Pagination 
            total={pagination?.totalSize} 
            pageSize={pagination?.limit}
            onChange={((currentPage, limit) => { setPagination({ ...pagination, limit, currentPage }) })}
            current={pagination.currentPage} 
          />
        </Col>
      </Row>

      <OrdersList 
        orders={orders} 
        onSelectRows={selectedRowKeys => setSelectedRows(selectedRowKeys)}
        selectedRows={selectedRows}
        loading={isLoading}
        showButtons={{ download: true, markCancelledByUser: true, invoice: true }}
        showCustomConfig
        onMarkCancelled={() => fetchOrders()}
        
      />

      <Row justify="end">
        <Col>
          <Pagination 
            total={pagination?.totalSize} 
            pageSize={pagination?.limit}
            onChange={((currentPage, limit) => { setPagination({ ...pagination, limit, currentPage }) })}
            current={pagination.currentPage} 
          />
        </Col>
      </Row>
    </div>
  )
}

export default AllOrder